/**
 * Clearline redesign of the case-details page — the NEW design, mounted at
 * /cases/:caseId/redesign so it can be compared against the existing page at
 * /cases/:caseId for the same case.
 *
 * Imported from the wip/updatedui-redesign branch. Deliberately kept as a
 * parallel file rather than replacing src/pages/CaseDetails.js: the two designs
 * must render side by side, and four of the components it depends on
 * (ImageLightbox, DamagedPartsModal, EditPartModal, CaptureIssues) diverged
 * between the branches. Those live in ./components/ so adopting them here
 * cannot alter the old design and invalidate the comparison.
 *
 * Shared modules that were strict supersets — utils/carPartMapping.js — were
 * taken wholesale instead. statusMapping.js gained only its two clearline
 * helpers, so the Unknown-status handling on this branch is preserved.
 *
 * Delete this directory and its route to drop the comparison.
 */
import { Link, useLocation, useParams } from "react-router-dom";
import { countryFromSearch } from "../utils/caseHref";
import { formatUploadDuration } from "../utils/uploadDuration";
import { decodeRefNum } from "../utils/refNumCrypto";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Camera,
  Car,
  CheckCircle2,
  Clock,
  Download,
  Globe,
  Hash,
  Inbox,
  MousePointerClick,
  Pencil,
  Tag,
} from "lucide-react";
import {
  getAuthToken,
  getSourceName,
  getUserProfile,
} from "../services/authService";
import {
  getAssessmentDetail,
  getDigitalPassport,
  getDigitalPassportReport,
  getPoiTracking,
  getUploads,
} from "../services/apiService";
import { toPoiModel } from "./poiTracking";
import { getApiGeneration } from "../services/apiGeneration";
import PointOfImpact from "./components/PointOfImpact";
import DigitalPassport from "./components/DigitalPassport";
import PassportAlert from "./components/PassportAlert";
import {
  toHistory as toPassportHistory,
  toComparison as toPassportComparison,
  toPassportAlert,
} from "./digitalPassport";
import SceneForensics from "./components/SceneForensics";
import BackToCases from "../components/BackToCases";
import {
  getStatusInfo,
  getStatusInfoFor,
  getClearlineStatusChip,
  formatFullDateTime,
  isFailedStatus,
} from "../utils/statusMapping";
import { buildWarningMessageList, hasNoLocationPermissionWarning } from "../utils/warnings";
import { partsOverview, hasPartsOverview } from "../utils/partsOverview";
import { sidesOverview, hasSidesOverview } from "../utils/sidesOverview";
import OverviewIdentifiedModal from "../components/OverviewIdentifiedModal";
import checkCircleGreen from "../assets/icons/check-circle-green-figma.svg";
import warningTriangleRed from "../assets/icons/warning-triangle-red.svg";
import { usePublishPageSections } from "../context/PageSectionsContext";
import { usePublishCammyPageSummary } from "../context/CammySpeechContext";
import {
  buildDamageModel,
  SEVERITY_META,
  NO_DAMAGE_COLOR,
} from "../utils/carPartMapping";
import { usePageConfig } from "../hooks/usePageConfig";
import {
  getApiBaseUrl,
  PORTAL_BASE_URL,
  withTrailingSlash,
} from "../config/urls";

import loadingIcon from "../assets/icons/cases/loading.svg";
import latviaFlag from "../assets/icons/cases/latvia-flag.svg";
import lithuaniaFlag from "../assets/icons/cases/lithuania-flag.svg";
import estoniaFlag from "../assets/icons/cases/estonia-flag.svg";

// Import components
import ImageLightbox from "./components/ImageLightbox";
import DamagedPartsModal from "./components/DamagedPartsModal";
import EditPartModal from "./components/EditPartModal";
import MetaItem from "./components/MetaItem";

import {
  extractSideFromName,
  prettySide,
  toCapturedImages,
  uploadsAreCategorised,
} from "./uploads";
import CarDamage3D, {
  DEFAULT_MODEL,
  MODEL_OPTIONS,
  locatableKeysFor,
} from "../components/CarDamage3D";
import CaptureIssues from "./components/CaptureIssues";
import LocationCheckModal from "../components/LocationCheckModal";
import ApproveCommentModal from "../components/ApproveCommentModal";
import { getApiSourceName, getClientEntry } from "../config/clientConfig";
import { mediaResolverFor } from "../utils/mediaResolver";
import { MediaUrlProvider } from "../context/MediaUrlContext";

/**
 * Placeholder-aware truthiness. The assessment payload and pick() both use "-",
 * "N/A" and "" for "not present", and all three are truthy in JS.
 */
const realValue = (value) => {
  if (value === null || value === undefined) return undefined;
  const text = String(value).trim();
  if (!text || text === "-" || /^(n\/?a|none|null|undefined)$/i.test(text)) {
    return undefined;
  }
  return text;
};

const DOCUMENTS_CACHE_NAME = "documents-tab-cache-v1";

const COUNTRY_FLAGS = {
  latvia: latviaFlag,
  lv: latviaFlag,
  lat: latviaFlag,
  lithuania: lithuaniaFlag,
  lt: lithuaniaFlag,
  ltu: lithuaniaFlag,
  estonia: estoniaFlag,
  ee: estoniaFlag,
  est: estoniaFlag,
};

/** @returns {string} flag asset URL, or "" when the country is unknown. */
const getCountryFlagSrc = (countryName) =>
  COUNTRY_FLAGS[
    String(countryName || "")
      .toLowerCase()
      .trim()
  ] || "";

const EMPTY_SENTINELS = new Set(["none", "null"]);

/** First non-empty value among `keys` on `source`, else "-". */
const pick = (source, keys) => {
  for (const key of keys) {
    const value = source?.[key];
    // "NONE" and "null" are real values this API sends for an unset field
    // (confirmed on vin_reading, and on make/model — the "null null" header
    // bug), not the words as data — same idea as property's lat/long arriving
    // as the literal string "None" for "no coordinates".
    if (
      value !== undefined &&
      value !== null &&
      String(value).trim() !== "" &&
      !EMPTY_SENTINELS.has(String(value).trim().toLowerCase())
    ) {
      return String(value);
    }
  }
  return "-";
};
const DOCUMENTS_CACHE_TTL_MS = 30 * 60 * 1000;

// Figma-aligned Case Details view (Tailwind). Sidebar removed to use app's global Layout.
/**
 * @param deployment  which Hegic backend this case lives on, when it is not
 *   the logged-in client's. Break-in runs the same contract on its own host
 *   with its own source_name and media, and has neither a digital passport nor
 *   a point of impact — so it turns those two off rather than showing tabs
 *   that 404. Omitted, everything resolves the session exactly as before.
 */
export default function CaseDetails({ deployment }) {
  /**
   * Config through the hook, not a direct import of the JSON.
   *
   * This page read the shared file straight off disk, so it saw neither the
   * portal-config record nor any per-client override — which is why enabling
   * Ergo's documents tab in its client file changed nothing here, and why the
   * POI and passport tabs had to be gated on the client contract in code
   * instead of on config.
   */
  const { pageConfig } = usePageConfig();

  // Ergo motor only, resolved the same safe way apiSourceName is elsewhere.
  // Used below to drop the "PDF Report" tile from the generic document-type
  // grid — that tile has no click handler at all, unlike the actual PDF
  // preview card further down, which fetches and shows the report and is
  // left untouched.
  const hidePdfReportBox =
    getClientEntry(getSourceName())?.sourceName === "ergobaltic";

  // The URL carries an obfuscated ref_num (utils/refNumCrypto), which landed on
  // dev after this page was branched — so it read the token as a literal
  // reference and every lookup came back empty rather than failing outright.
  const { caseId: _encodedCaseId } = useParams();
  const routerLocation = useLocation();
  const caseId = decodeRefNum(_encodedCaseId);

  // Debug: Log the received caseId (ref_num)

  const [tab, setTab] = useState(pageConfig.general.defaultTab); // ai | overview | images | documents
  // Images tab: which damage gallery is showing — inventory (one tile per
  // damaged side) or part-wise (one tile per damaged part), as in current
  // design's toggle of the same name.
  const [damageTab, setDamageTab] = useState("inventory"); // inventory | part-wise
  // Vehicle history, looked up on load rather than waiting to be asked for.
  const [passport, setPassport] = useState({
    loading: false,
    history: null,
    comparison: null,
    alert: null,
  });
  const [assessmentData, setAssessmentData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [aiSummaryData, setAiSummaryData] = useState(null);
  const [isAiSummaryLoading, setIsAiSummaryLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [partsListOpen, setPartsListOpen] = useState(false);
  const [sidesListOpen, setSidesListOpen] = useState(false);
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxCurrentIndex, setLightboxCurrentIndex] = useState(0);

  const [isSearchActive, setIsSearchActive] = useState(false);

  const [lightboxIsDamageImages, setLightboxIsDamageImages] = useState(false);
  const [isDocumentsLoading, setIsDocumentsLoading] = useState(false);
  const [documentsPreviewUrl, setDocumentsPreviewUrl] = useState("");
  const [documentsPreviewError, setDocumentsPreviewError] = useState("");
  const [documentsArchiveName, setDocumentsArchiveName] = useState("");

  // Approve flow: capture reviewer comment
  const [approveCommentOpen, setApproveCommentOpen] = useState(false);

  // Damage report (3D) state — selection is owned here, the viewer is controlled.
  const [selectedPart, setSelectedPart] = useState(null);
  const [poi, setPoi] = useState(null);
  const [isPoiLoading, setIsPoiLoading] = useState(false);
  const [viewPreset, setViewPreset] = useState("threeq");
  const [vehicleModel, setVehicleModel] = useState(DEFAULT_MODEL);
  const [editPartOpen, setEditPartOpen] = useState(false);

  // Damaged Parts Modal state
  const [damagedPartsModalState, setDamagedPartsModalState] = useState({
    isOpen: false,
    initialFilter: "all",
    initialSeverityFilter: "",
  });

  // Helper functions to open modal with specific filters
  const openDamagedPartsModal = (
    initialFilter = "all",
    initialSeverityFilter = "",
  ) => {
    setDamagedPartsModalState({
      isOpen: true,
      initialFilter,
      initialSeverityFilter,
    });
  };

  const closeDamagedPartsModal = () => {
    setDamagedPartsModalState({
      isOpen: false,
      initialFilter: "all",
      initialSeverityFilter: "",
    });
  };

  /**
   * A failed capture has no point of impact and no history worth showing: the
   * assessment never completed, so both tabs would be describing damage on a
   * vehicle that was never assessed.
   *
   * Read from assessmentData rather than the `info` alias below, because the
   * effects that gate on these run earlier in the file and a const cannot be
   * used before it is defined.
   */
  const caseFailed = isFailedStatus(assessmentData?.info?.claim_state);

  // Documents (the PDF report) only exists once a case is fully reviewed —
  // matching CaseDetails.js's isDocumentsTabEnabled/isApproved. This file
  // gated the tab purely on pageConfig.tabs.documents.enabled, a per-client
  // flag with no notion of the case's own status, so Ergo's Documents tab
  // showed (and tried to preview a report for) every case regardless of
  // claim_state, not just Review Completed ones.
  const isApproved = parseInt(assessmentData?.info?.claim_state, 10) === 8;

  /**
   * A named deployment is v2 by definition, and says for itself which of the
   * two extra tabs its backend has. Break-in has neither: /poi_tracking 404s
   * there and the digital passport is served by the claims portal.
   */
  const isV2 = deployment ? true : getApiGeneration() === "v2";
  const showPoi =
    isV2 && !caseFailed && (deployment ? Boolean(deployment.features?.poi) : true);
  const showPassport =
    isV2 && !caseFailed && (deployment ? Boolean(deployment.features?.passport) : true);

  /**
   * /ai_summary is fetched on mount, not on tab open, so a deployment without
   * the route 404s on every case load. The tab's own gate is the logged-in
   * client's config, which is the wrong client when the page is pointed
   * elsewhere — hence the deployment's say here too.
   */
  const showAiSummary =
    pageConfig.tabs.aiSummary.enabled &&
    (deployment ? Boolean(deployment.features?.aiSummary) : true);

  /**
   * The backend this case belongs to.
   *
   * The four raw fetches below resolved getApiBaseUrl() — the LOGGED-IN
   * client's host — which is right until the page is pointed at another
   * deployment. Approving a break-in case would then have POSTed the review
   * and PUT claim_state 8 to the claims host, against a ref_num it does not
   * have: a cross-tenant write that fails silently or, worse, does not.
   */
  const apiBase = deployment?.apiHost || getApiBaseUrl();
  const apiSource = deployment?.sourceName || getApiSourceName();

  // Memoised because it now feeds dependency arrays and the provider below.
  // `deployment` is a frozen module constant or undefined, so it is stable.
  const getMediaUrl = useMemo(() => mediaResolverFor(deployment), [deployment]);

  // A photo can turn up under two different labels for the same side —
  // all_damages and roi_images don't agree on side spelling ("front_left" vs
  // "frontLeft") — so keying on the raw side string plus url (as this used
  // to) let the same photo through twice, once under each source's own
  // spelling: same picture, "FRONT_LEFT" and "FRONTLEFT" as two separate
  // Inventory of Damages tiles. Same approach as CaseDetails.js's
  // getImageIdentity: identify primarily by the image's own URL (query
  // string dropped, so a cache-busting param isn't a different photo),
  // falling back to a normalised side + id only when there's no URL to go
  // on at all.
  const getImageIdentity = (image) => {
    const imageUrl = (image.imageUrl || image.src || "").toString().trim();

    if (imageUrl) {
      try {
        const url = new URL(imageUrl);
        return `${url.origin}${url.pathname}`;
      } catch (error) {
        return imageUrl;
      }
    }

    const normalizedSide = (image.side || image.title || "")
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, "")
      .replace(/^back/, "rear");
    return normalizedSide && image.id ? `${normalizedSide}|${image.id}` : "";
  };

  const getUniqueImages = (images) => {
    const seen = new Set();

    return images.filter((image) => {
      const key = getImageIdentity(image);
      if (!key) return false;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const handleApproveClick = () => {
    setApproveCommentOpen(true);
  };

  const handleApproveSubmit = async (comment) => {
    const token = getAuthToken();
    if (!token) {
      console.error("No authentication token available");
      return;
    }

    const profile = getUserProfile();
    const userEmail = profile?.email || getClientEntry(localStorage.getItem("source_name"))?.fallbackEmail || "camcom@camcom.ai";

    const refNum = assessmentData?.info?.ref_num || caseId;
    const vehicleId = assessmentData?.info?.id;

    if (!vehicleId) {
      console.error("Missing vehicle id (assessmentData.info.id)");
      return;
    }

    const payload = {
      data: [],
      hello: "world",
      reviewerComment: {
        comment: comment,
        ref_num: refNum,
        source_name: apiSource,
        vehicle_id: vehicleId,
        user_id: userEmail,
        user_type: 10,
      },
      verified_list: [],
      approve: true,
    };

    try {
      const reviewHistoryReq = fetch(
        `${apiBase}/assess_update_review_history`,
        {
          method: "POST",
          headers: {
            Accept: "*/*",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
          },
          body: `data=${encodeURIComponent(JSON.stringify(payload))}`,
        },
      );

      const vehicleUpdateBody = new URLSearchParams({
        keyValue: JSON.stringify({ claim_state: 8 }),
        ref_num: refNum,
        source_name: apiSource,
        user_id: userEmail,
      });

      const vehicleUpdateReq = fetch(`${apiBase}/vehicle/${vehicleId}/`, {
        method: "PUT",
        headers: {
          Accept: "*/*",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
        },
        body: vehicleUpdateBody.toString(),
      });

      const [reviewRes, vehicleRes] = await Promise.all([
        reviewHistoryReq,
        vehicleUpdateReq,
      ]);

      const reviewOk = reviewRes.ok;
      const vehicleOk = vehicleRes.ok;

      if (!reviewOk) {
        const text = await reviewRes.text().catch(() => "");
        console.error("Failed to submit approve note:", reviewRes.status, text);
      }

      if (!vehicleOk) {
        const text = await vehicleRes.text().catch(() => "");
        console.error(
          "Failed to update vehicle claim_state:",
          vehicleRes.status,
          text,
        );
      }

      // Refresh case details only when both APIs succeeded
      if (reviewOk && vehicleOk) {
        callAssessmentAPI(refNum).catch((e) =>
          console.error("Error refreshing assessment after approval:", e),
        );
      }
    } catch (e) {
      console.error("Error submitting approval APIs:", e);
    }
  };

  // Function to refresh assessment data after damage updates
  const refreshAssessmentData = async () => {
    if (caseId) {
      await callAssessmentAPI(caseId);
    }
  };

  // API call function for assessment
  const callAssessmentAPI = async (refNum) => {
    setIsLoading(true);
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error("No authentication token available");
      }

      // Routed through the shared service rather than fetching directly: this
      // file arrived from a branch that predates multi-client support and had
      // source_name hardcoded to "ergobaltic", which would return nothing for
      // Bajaj or Hegic. getAssessmentDetail resolves the client's own host,
      // source_name and API generation, so this page and the existing one show
      // the same data for the same case — the point of the comparison.
      const data = await getAssessmentDetail(refNum, deployment);
      setAssessmentData(data);
      return data;
    } catch (error) {
      console.error("Assessment API Call Failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // API call function for AI summary
  const callAISummaryAPI = async (refNum) => {
    setIsAiSummaryLoading(true);
    try {
      const token = getAuthToken();
      if (!token) {
        throw new Error("No authentication token available");
      }

      const formData = new FormData();
      formData.append("ref_num", refNum);
      formData.append("source_name", apiSource);

      const response = await fetch(`${apiBase}/ai_summary`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        const transformedData = transformAiSummaryData(data);
        setAiSummaryData(transformedData);
        return data;
      } else {
        console.error(
          "AI Summary API Error:",
          response.status,
          response.statusText,
        );
        if (response.status === 401) {
        }
      }
    } catch (error) {
      console.error("AI Summary API Call Failed:", error);
    } finally {
      setIsAiSummaryLoading(false);
    }
  };

  // Transform AI summary API response to our card format
  const transformAiSummaryData = (rawData) => {
    if (!rawData) return null;

    return {
      vehicle_overview: {
        details: [
          `${rawData.car_details?.make || "Unknown"} ${
            rawData.car_details?.model || "Unknown"
          } ${rawData.car_details?.vehicle_type || ""}`.trim(),
          `Primary Color: ${rawData.color_info?.primary_color || "Unknown"} ${
            rawData.color_info?.is_metallic
              ? "(Metallic)"
              : rawData.color_info?.is_matte
                ? "(Matte)"
                : ""
          }`.trim(),
          `Origin: ${rawData.car_details?.origin || "Unknown"}`,
          `Engine Status: ${
            rawData.vehicle_readings?.engine_working ? "Working" : "Not Working"
          }`,
        ].filter((detail) => detail && !detail.includes("Unknown Unknown")),
      },
      identification: {
        details: [
          ...(rawData.license_plate?.is_visible
            ? [
                `License Plate: ${rawData.license_plate.number} (${rawData.license_plate.country_format})`,
                `Plate Readability: ${rawData.license_plate.readability}`,
              ]
            : ["License Plate: Not Visible"]),
          ...(rawData.vin_info?.is_visible
            ? [
                `VIN: ${rawData.vin_info.number}`,
                `VIN Location: ${rawData.vin_info.location}`,
                `VIN Readability: ${rawData.vin_info.readability}`,
              ]
            : ["VIN: Not Visible"]),
          ...(rawData.vehicle_readings?.odometer_visible
            ? [`Odometer: ${rawData.vehicle_readings.odometer_reading}`]
            : []),
        ],
      },
      environment_quality: {
        details: [
          `Environment: ${
            rawData.background_details?.environment || "Unknown"
          } - ${rawData.background_details?.location_type || "Unknown"}`,
          `Time: ${
            rawData.background_details?.time_of_day || "Unknown"
          }, Weather: ${rawData.background_details?.weather || "Unknown"}`,
          `Exposure: ${
            rawData.exposure_info?.description || "Unknown"
          } (Level: ${rawData.exposure_info?.level || "N/A"})`,
          ...(
            rawData.background_details?.notable_elements?.slice(0, 2) || []
          ).map((element) => `Notable: ${element}`),
        ],
      },
      authenticity_check: {
        status: rawData.is_real_image ? "pass" : "fail",
        details: [
          `Real Image: ${rawData.is_real_image ? "Yes" : "No"}`,
          `Reasoning: ${rawData.reasoning || "No reasoning provided"}`,
          `Vehicle Consistency: ${
            rawData.vehicle_consistency?.same_vehicle_across_images
              ? "Consistent"
              : "Inconsistent"
          }`,
          `Confidence Level: ${
            rawData.vehicle_consistency?.confidence_level || "Unknown"
          }`,
        ],
      },
      image_quality: {
        status:
          rawData.image_quality?.quality_issues?.length === 0 &&
          !rawData.image_quality?.has_blur &&
          !rawData.image_quality?.low_light
            ? "pass"
            : "fail",
        details: [
          `Blur Detected: ${rawData.image_quality?.has_blur ? "Yes" : "No"}`,
          `Low Light: ${rawData.image_quality?.low_light ? "Yes" : "No"}`,
          `IoI Pattern: ${
            rawData.image_quality?.has_ioi_pattern ? "Yes" : "No"
          }`,
          `Quality Issues: ${
            rawData.image_quality?.quality_issues?.length
              ? rawData.image_quality.quality_issues.join(", ")
              : "None"
          }`,
        ],
      },
      vehicle_condition: {
        status:
          !rawData.is_dirty && !rawData.has_obstructions ? "pass" : "warning",
        details: [
          `Cleanliness: ${rawData.cleanliness_info?.status || "Unknown"}`,
          `Dirty: ${rawData.is_dirty ? "Yes" : "No"}`,
          `Obstructions: ${rawData.has_obstructions ? "Yes" : "No"}`,
          `Reflective Surface: ${rawData.is_reflective ? "Yes" : "No"}`,
          ...(
            rawData.modifications_detected
              ?.filter((mod) => mod !== "none")
              .slice(0, 2) || []
          ).map((mod) => `Modification: ${mod}`),
        ],
      },
    };
  };

  /**
   * Look the vehicle's history up on load instead of waiting to be asked.
   *
   * Two requests at most, and the second only when it can answer something: the
   * query says whether any inspection predates this claim, and only then is the
   * report worth fetching. Both are advisory — a failure leaves the tab exactly
   * as it was, to be opened by hand.
   */
  useEffect(() => {
    if (!showPassport) return undefined;
    const vehicle = assessmentData?.info;
    if (!caseId || !vehicle) return undefined;
    // Nothing will show it, so nothing should fetch it.
    if (isFailedStatus(vehicle.claim_state)) return undefined;

    // pick() yields "-" for missing fields, which is truthy; searching for it
    // returns the whole fleet.
    const real = (value) => {
      const text = typeof value === "string" ? value.trim() : value;
      return text && text !== "-" && text !== "N/A" ? String(text) : "";
    };
    const plate =
      real(vehicle.num_plate) ||
      real(vehicle.number_plate) ||
      real(vehicle.registration_no);
    const vinNo =
      real(vehicle.vin) ||
      real(vehicle.vin_reading) ||
      real(vehicle.vin_no) ||
      real(vehicle.chassis_no);
    if (!plate && !vinNo) return undefined;

    let cancelled = false;
    setPassport((current) => ({ ...current, loading: true }));

    (async () => {
      try {
        const raw = await getDigitalPassport({
          refNum: caseId,
          searchParam: plate ? 2 : 1,
          numPlate: plate,
          vin: plate ? "" : vinNo,
        });
        const history = toPassportHistory(raw, { currentRefNum: caseId });
        let alert = toPassportAlert(history);
        let comparison = null;

        if (alert?.kind === "earlier_unknown" && alert.nearestEarlier) {
          try {
            comparison = toPassportComparison(
              await getDigitalPassportReport({
                refNum: alert.nearestEarlier.refNum,
                sourceName: alert.nearestEarlier.sourceName,
                currentRefNum: caseId,
              }),
            );
            alert = toPassportAlert(history, { comparison });
          } catch (err) {
            // Keep the "there is earlier history" banner; only the verdict is lost.
            console.info("[Passport] comparison unavailable:", err.message);
          }
        }

        if (!cancelled) setPassport({ loading: false, history, comparison, alert });
      } catch (err) {
        console.info("[Passport] history unavailable:", err.message);
        if (!cancelled) {
          setPassport({ loading: false, history: null, comparison: null, alert: null });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [caseId, assessmentData, showPassport, deployment]);

  // Call APIs when component mounts and caseId is available
  useEffect(() => {
    if (caseId) {
      // Call both APIs
      callAssessmentAPI(caseId);
      if (showAiSummary) callAISummaryAPI(caseId);
    }
  }, [caseId, showAiSummary]);

  /**
   * What the user actually photographed.
   *
   * A v2 assessment carries only the ROI set — the crops the model worked on —
   * so the Images tab was showing the pipeline's output where a reviewer
   * expects the originals. GET /uploads has them, 29 against a handful for the
   * case probed, and they carry real category tokens, which is what makes the
   * tab group them instead of listing them flat.
   */
  const [userUploads, setUserUploads] = useState([]);

  useEffect(() => {
    setUserUploads([]);
    if (!caseId || getApiGeneration() !== "v2") return undefined;
    let cancelled = false;

    getUploads(caseId, deployment)
      .then((rows) => {
        if (!cancelled) setUserUploads(rows);
      })
      .catch((error) => {
        // The tab still has the ROI set to fall back on, so this is a
        // degradation rather than a failure.
        console.warn("[CaseDetails] uploads unavailable:", error?.message);
      });

    return () => {
      cancelled = true;
    };
  }, [caseId, deployment]);

  /** The originals when we have them, the assessment's set otherwise. */
  const uploadRows = userUploads.length ? userUploads : assessmentData?.uploads || [];

  // Same count as current design's Images tab badge (getUploadedImageCount):
  // only rows that actually carry a picture, not every row uploads returns.
  const userCapturedImageCount = uploadRows.filter(
    (upload) => upload?.upload_picture || upload?.picture,
  ).length;

  // Helper function to categorize uploads based on name field
  const categorizeUploads = () => {
    if (!uploadRows.length) {
      return { exteriors: [], closeups: [], identifiers: [] };
    }

    const categories = {
      exteriors: [],
      closeups: [],
      identifiers: [],
    };
    // The classification below is if/else-if/else — each row lands in
    // exactly one bucket, so it can't itself produce a duplicate. A photo
    // rendered twice under Exterior/Closeup means uploadRows carried the
    // same row twice (id/picture both repeated) — seen here rather than
    // asking every caller of categorizeUploads to dedupe it separately.
    const seenUploads = new Set();

    uploadRows.forEach((upload) => {
      const uploadKey = upload?.id ?? upload?.picture ?? null;
      if (uploadKey !== null) {
        if (seenUploads.has(uploadKey)) return;
        seenUploads.add(uploadKey);
      }

      // Same precedence as current design's equivalent cards: a real `side`
      // field wins, then the part name on a close-up, then whatever side is
      // baked into the file name — never a bare "Image N" while any of those
      // three exist.
      const sideLabel =
        upload.side ||
        upload.part_name ||
        prettySide(extractSideFromName(upload.name));
      const imageData = {
        id: upload.id,
        name: upload.name,
        title: sideLabel,
        side: sideLabel,
        imageUrl: getMediaUrl(upload.picture),
        fileName: upload.file_name || "Unknown",
      };

      // Classify based on name field
      if (upload.name === "car_image") {
        categories.exteriors.push(imageData);
      } else if (upload.name === "part_image") {
        categories.closeups.push(imageData);
      } else {
        categories.identifiers.push(imageData);
      }
    });

    return categories;
  };

  /** Every upload, flat, when the API never said which bucket they belong in. */
  const getCapturedImages = () => toCapturedImages(uploadRows, getMediaUrl);

  // Inventory of Damages is one tile per annotated damage photo (all_damages),
  // matching CaseDetails.js's getInventoryOfDamagesImages. roi_images is not a
  // second, independent set of photos to show alongside them: it is the same
  // photos again, before annotation — ImageLightbox's own Annotations toggle
  // already looks a roi_images entry up by side (sameSide) to show a damage
  // tile's un-annotated original. Pushing roi_images in here too showed every
  // damaged side twice: once annotated, once as its own "duplicate" tile.
  const getDamageGalleryImages = () => {
    if (!assessmentData) return [];

    const damageImages =
      assessmentData.all_damages
        ?.filter((damage) => damage.picture)
        .map((damage) => ({
          id: damage.id,
          side: damage.side,
          imageUrl: getMediaUrl(damage.picture),
          title: damage.side,
          source: "all_damages",
        })) || [];

    return getUniqueImages(damageImages);
  };

  // Part-wise Damages: the same photos, grouped by the part they were taken
  // of (assessment_details[].damage_images) rather than by side — matching
  // CaseDetails.js's getPartWiseDamageImages, and the "Part-wise Damages"
  // toggle current design offers beside "Inventory of Damages".
  const getPartWiseDamageImages = () => {
    if (!assessmentData) return [];

    const partImages = (assessmentData.assessment_details || []).flatMap(
      (part, partIndex) => {
        const images = part.damage_images || part.damageImages || [];
        return images
          .filter((image) => image.picture)
          .map((image, imageIndex) => ({
            id:
              image.id ||
              image.damage_id ||
              `${part.part_name || "part-damage"}-${partIndex}-${imageIndex}`,
            side: image.side || part.side,
            imageUrl: getMediaUrl(image.picture),
            // A damage_images entry carries its own pre-annotation photo —
            // unlike all_damages, which needs the side-matched lookup into
            // roi_images that ImageLightbox's Annotations toggle does. Set
            // directly, it short-circuits that lookup (checked first, same
            // as CaseDetails.js's getOriginalDamageImagePath), which is the
            // only reason the toggle worked for Inventory of Damages but not
            // here: nothing on this tile gave it anything to switch to.
            originalImageUrl: getMediaUrl(
              image.upload_picture ||
                image.original_picture ||
                image.original_uploaded_img_path ||
                image.picture,
            ),
            title: part.part_name || image.side || part.side,
            source: "damage_images",
          }));
      },
    );

    return getUniqueImages(partImages);
  };

  // Helper function to open lightbox with specific image set
  const openLightbox = (images, startIndex = 0, isDamageImages = false) => {
    setLightboxImages(images);
    setLightboxCurrentIndex(startIndex);
    setLightboxIsDamageImages(isDamageImages);
    setLightboxOpen(true);
  };

  // Helper function to close lightbox
  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImages([]);
    setLightboxCurrentIndex(0);
    setLightboxIsDamageImages(false);
  };

  // Helper function to get all images for lightbox
  const getAllImagesForLightbox = () => {
    const allImages = [];

    // Add damage images from API
    allImages.push(...getDamageGalleryImages());

    // Add upload images
    getCapturedImages().forEach((upload) => {
      allImages.push({
        id: upload.id,
        imageUrl: upload.imageUrl,
        title: upload.title,
        side: upload.name,
        source: "uploads",
      });
    });

    return allImages;
  };

  const getDocumentsCacheKey = (refNum) =>
    `https://documents-cache.local/${encodeURIComponent(refNum)}/master-data`;

  const getDocumentsMetaKey = (refNum) => `documents-tab-cache-meta:${refNum}`;

  const readZipText = (bytes) => {
    try {
      return new TextDecoder().decode(bytes);
    } catch (error) {
      let result = "";
      bytes.forEach((byte) => {
        result += String.fromCharCode(byte);
      });
      return result;
    }
  };

  const inflateZipEntry = async (compressedBytes, compressionMethod) => {
    if (compressionMethod === 0) {
      return compressedBytes.buffer.slice(
        compressedBytes.byteOffset,
        compressedBytes.byteOffset + compressedBytes.byteLength,
      );
    }

    if (compressionMethod === 8) {
      const decompressionStream = new DecompressionStream("deflate-raw");
      return await new Response(
        new Blob([compressedBytes]).stream().pipeThrough(decompressionStream),
      ).arrayBuffer();
    }

    throw new Error(`Unsupported ZIP compression method: ${compressionMethod}`);
  };

  const extractPdfFromZipBlob = async (zipBlob) => {
    const buffer = await zipBlob.arrayBuffer();
    const uint8 = new Uint8Array(buffer);
    const view = new DataView(buffer);

    let endOfCentralDirOffset = -1;
    const searchStart = Math.max(0, uint8.length - 65557);

    for (let i = uint8.length - 22; i >= searchStart; i -= 1) {
      if (view.getUint32(i, true) === 0x06054b50) {
        endOfCentralDirOffset = i;
        break;
      }
    }

    if (endOfCentralDirOffset === -1) {
      throw new Error("ZIP central directory not found");
    }

    const totalEntries = view.getUint16(endOfCentralDirOffset + 10, true);
    let centralDirectoryOffset = view.getUint32(
      endOfCentralDirOffset + 16,
      true,
    );

    for (let entryIndex = 0; entryIndex < totalEntries; entryIndex += 1) {
      if (view.getUint32(centralDirectoryOffset, true) !== 0x02014b50) {
        throw new Error("Invalid ZIP central directory entry");
      }

      const compressionMethod = view.getUint16(
        centralDirectoryOffset + 10,
        true,
      );
      const compressedSize = view.getUint32(centralDirectoryOffset + 20, true);
      const fileNameLength = view.getUint16(centralDirectoryOffset + 28, true);
      const extraLength = view.getUint16(centralDirectoryOffset + 30, true);
      const commentLength = view.getUint16(centralDirectoryOffset + 32, true);
      const localHeaderOffset = view.getUint32(
        centralDirectoryOffset + 42,
        true,
      );

      const fileNameBytes = uint8.slice(
        centralDirectoryOffset + 46,
        centralDirectoryOffset + 46 + fileNameLength,
      );
      const fileName = readZipText(fileNameBytes);

      centralDirectoryOffset +=
        46 + fileNameLength + extraLength + commentLength;

      if (!fileName.toLowerCase().endsWith(".pdf")) {
        continue;
      }

      if (view.getUint32(localHeaderOffset, true) !== 0x04034b50) {
        throw new Error("Invalid ZIP local file header");
      }

      const localFileNameLength = view.getUint16(localHeaderOffset + 26, true);
      const localExtraLength = view.getUint16(localHeaderOffset + 28, true);
      const fileDataStart =
        localHeaderOffset + 30 + localFileNameLength + localExtraLength;
      const compressedBytes = uint8.slice(
        fileDataStart,
        fileDataStart + compressedSize,
      );
      const pdfBuffer = await inflateZipEntry(
        compressedBytes,
        compressionMethod,
      );

      return {
        blob: new Blob([pdfBuffer], { type: "application/pdf" }),
        fileName,
      };
    }

    throw new Error("No PDF file found in ZIP archive");
  };

  const getCachedDocumentsArchive = async (refNum) => {
    if (!("caches" in window)) {
      return null;
    }

    const metaValue = window.localStorage.getItem(getDocumentsMetaKey(refNum));
    if (!metaValue) {
      return null;
    }

    try {
      const meta = JSON.parse(metaValue);
      if (
        !meta.cachedAt ||
        Date.now() - meta.cachedAt > DOCUMENTS_CACHE_TTL_MS
      ) {
        window.localStorage.removeItem(getDocumentsMetaKey(refNum));
        const cache = await window.caches.open(DOCUMENTS_CACHE_NAME);
        await cache.delete(getDocumentsCacheKey(refNum));
        return null;
      }

      const cache = await window.caches.open(DOCUMENTS_CACHE_NAME);
      const response = await cache.match(getDocumentsCacheKey(refNum));
      if (!response) {
        return null;
      }

      return {
        blob: await response.blob(),
        fileName: meta.fileName || `report_${refNum}.zip`,
      };
    } catch (error) {
      console.error("Error reading documents cache:", error);
      return null;
    }
  };

  const cacheDocumentsArchive = async (refNum, blob, fileName) => {
    if (!("caches" in window)) {
      return;
    }

    const cache = await window.caches.open(DOCUMENTS_CACHE_NAME);
    await cache.put(
      getDocumentsCacheKey(refNum),
      new Response(blob, {
        headers: {
          "Content-Type": blob.type || "application/zip",
        },
      }),
    );

    window.localStorage.setItem(
      getDocumentsMetaKey(refNum),
      JSON.stringify({
        cachedAt: Date.now(),
        fileName,
      }),
    );
  };

  const fetchDocumentsArchive = async (forceRefresh = false) => {
    const refNum = assessmentData?.info?.ref_num || caseId;
    const sourceName = apiSource;

    if (!refNum) {
      throw new Error("Reference number not found");
    }

    if (!forceRefresh) {
      const cachedArchive = await getCachedDocumentsArchive(refNum);
      if (cachedArchive) {
        return {
          ...cachedArchive,
          refNum,
        };
      }
    }

    const formData = new FormData();
    formData.append("ref_num", refNum);
    formData.append("source_name", sourceName);

    const token = getAuthToken();
    if (!token) {
      throw new Error("Authentication required");
    }

    const response = await fetch(`${apiBase}/download_master_data`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "*/*",
        Referer: withTrailingSlash(PORTAL_BASE_URL),
        "User-Agent":
          "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/143.0.0.0 Safari/537.36",
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data.url) {
      throw new Error("Download URL not found in response");
    }

    const fileResponse = await fetch(data.url);
    if (!fileResponse.ok) {
      throw new Error(`Failed to download file: ${fileResponse.status}`);
    }

    const blob = await fileResponse.blob();
    const fileName = `report_${refNum}.zip`;
    await cacheDocumentsArchive(refNum, blob, fileName);

    return {
      blob,
      refNum,
      fileName,
    };
  };

  useEffect(() => {
    return () => {
      if (documentsPreviewUrl) {
        window.URL.revokeObjectURL(documentsPreviewUrl);
      }
    };
  }, [documentsPreviewUrl]);

  useEffect(() => {
    setDocumentsArchiveName("");
    setDocumentsPreviewError("");
    setDocumentsPreviewUrl((prevUrl) => {
      if (prevUrl) {
        window.URL.revokeObjectURL(prevUrl);
      }
      return "";
    });
  }, [caseId]);

  useEffect(() => {
    const shouldLoadDocumentsPreview =
      tab === "documents" &&
      pageConfig.tabs.documents.enabled &&
      isApproved &&
      pageConfig.tabs.documents.documentTypes.pdfReport.enabled;

    if (!shouldLoadDocumentsPreview) {
      return;
    }

    let isCancelled = false;

    const loadDocumentsPreview = async () => {
      setIsDocumentsLoading(true);
      setDocumentsPreviewError("");

      try {
        const archive = await fetchDocumentsArchive(false);
        const pdfAsset = await extractPdfFromZipBlob(archive.blob);
        if (isCancelled) {
          return;
        }

        setDocumentsArchiveName(archive.fileName);
        setDocumentsPreviewUrl((prevUrl) => {
          if (prevUrl) {
            window.URL.revokeObjectURL(prevUrl);
          }
          return window.URL.createObjectURL(pdfAsset.blob);
        });
      } catch (error) {
        if (isCancelled) {
          return;
        }

        console.error("Error loading documents preview:", error);
        setDocumentsPreviewError(
          "Preview unavailable. You can still use Download Images and Report.",
        );
        setDocumentsPreviewUrl((prevUrl) => {
          if (prevUrl) {
            window.URL.revokeObjectURL(prevUrl);
          }
          return "";
        });
      } finally {
        if (!isCancelled) {
          setIsDocumentsLoading(false);
        }
      }
    };

    loadDocumentsPreview();

    return () => {
      isCancelled = true;
    };
  }, [tab, caseId, assessmentData]);

  const enabledDocumentsCount = Object.values(
    pageConfig.tabs.documents.documentTypes,
  ).filter((documentType) => documentType.enabled).length;

  // Function to download PDF report
  const downloadPDFReport = async () => {
    try {
      const archive = await fetchDocumentsArchive(false);
      const url = window.URL.createObjectURL(archive.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = archive.fileName;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      alert("Failed to download PDF report. Please try again.");
    }
  };

  /* ── Clearline view model ─────────────────────────────────────────────
     Everything the mock-3a chrome renders, derived from assessmentData. */

  const info = assessmentData?.info;
  const summary = assessmentData?.summary;

  const refNum = info?.ref_num || caseId;
  const numPlate = info?.num_plate?.trim() || "";
  // The registration leads the page; the reference number moves to the meta
  // row. Falls back to the reference when a case has no plate on record.
  const primaryId = numPlate || refNum;


  // The deployment's own wording where it has one — break-in calls state 3
  // "Completed" where claims calls it "AI Assessed", and the detail page must
  // not contradict the list it was opened from.
  const statusInfo = getStatusInfoFor(
    info?.claim_state != null ? parseInt(info.claim_state, 10) : 3,
    deployment?.statusLabels || getClientEntry(getSourceName())?.statusLabels,
  );
  const statusChip = getClearlineStatusChip(statusInfo.text);

  // Same source as the case list and the header's CaptureIssues pill — see
  // src/redesign/components/CaptureIssues.js.
  const warningCount = buildWarningMessageList(assessmentData).length;

  // assessment_details → 3D damage map, damaged-parts list, worst part.
  // Coverage follows the chosen mesh: each model separates a different set of
  // panels, so parts outside the active one are flagged unlocatable rather
  // than silently dead.
  const locatableKeys = useMemo(
    () => locatableKeysFor(vehicleModel),
    [vehicleModel],
  );
  const damage = useMemo(
    () =>
      buildDamageModel(assessmentData, {
        locatableKeys,
        // Without this the panel photos resolve against the logged-in client
        // and 404 on every break-in case, hiding themselves as they go.
        resolveMediaUrl: getMediaUrl,
      }),
    [assessmentData, locatableKeys, getMediaUrl],
  );

  // Point of impact — its own endpoint, and v2-only, so a failure here must not
  // disturb the rest of the page.
  //
  // A failed case consumes it nowhere: the POI tab is hidden and so is the
  // forensics block, which were its only two readers. The guard rarely fires
  // in practice — for Hegic the claim_state arrives with GET /vehicle, usually
  // after this has already gone out — but it stops the refetch once the state
  // is known, and cancels the pending one.
  useEffect(() => {
    if (!caseId || !showPoi) return;
    let cancelled = false;
    setIsPoiLoading(true);
    getPoiTracking(caseId)
      .then((envelope) => {
        if (!cancelled) setPoi(toPoiModel(envelope));
      })
      .catch((err) => {
        console.info("[POI] unavailable:", err.message);
        if (!cancelled) setPoi(null);
      })
      .finally(() => {
        if (!cancelled) setIsPoiLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [caseId, showPoi, deployment]);

  // Default the selection to the worst damaged panel once data lands.
  useEffect(() => {
    setSelectedPart((current) => {
      if (current && damage.byKey[current]) return current;
      return damage.worstKey;
    });
  }, [damage]);

  const selectedDetail = selectedPart ? damage.byKey[selectedPart] : null;

  const selectPart = useCallback(
    (key) => {
      if (!key) return setSelectedPart(null);
      // The mesh has pickable panels the assessment never reports (the coupe's
      // one-piece body shell). Ignore those rather than blanking the card.
      if (!damage.byKey[key]) return;
      setSelectedPart(key);
    },
    [damage],
  );

  // "null null" was shown here on a case missing both — the API sends the
  // literal string "null" for an unset make/model, not an empty/absent
  // field, which `.filter(Boolean)` doesn't catch (a non-empty string is
  // truthy). Shown as explicit "Make:"/"Model:" labels now, falling back to
  // "NA" each independently rather than silently dropping just the one that's
  // missing from a combined string.
  const pickOrNA = (keys) => {
    const value = pick(info, keys);
    return value === "-" ? "NA" : value;
  };
  const make = pickOrNA(["make"]);
  const model = pickOrNA(["model"]);
  const countryName = pick(
    {
      ...info,
      list_country: countryFromSearch(routerLocation.search),
    },
    ["country_corp_id", "country", "country_name", "list_country"],
  );
  const countryFlag = countryName === "-" ? "" : getCountryFlagSrc(countryName);

  // "00 hour 01 minutes 32 seconds" — sent pre-formatted, confirmed by a
  // captured /assessment_new response; shown as-is rather than re-parsed.
  const timeTakenToUpload = info?.time_taken_to_upload || "";

  const receivedAt = info?.created_at
    ? formatFullDateTime(info.created_at)
    : "";

  // The AI summary API reports a qualitative confidence level, not a percentage.
  const aiConfidence =
    aiSummaryData?.authenticity_check?.details
      ?.find((detail) => detail.startsWith("Confidence Level:"))
      ?.replace("Confidence Level:", "")
      .trim() || "";

  /**
   * Counted on every render, deliberately not memoised.
   *
   * It used to be a useMemo keyed on [assessmentData] with the exhaustive-deps
   * rule silenced. That was fine until the uploads moved to their own request:
   * uploadRows then changed AFTER assessmentData last did, the memo never
   * recomputed, and the tab read "Images 0" beside a grid full of pictures.
   *
   * The count has to come from exactly what the tab renders, and
   * getCapturedImages() is itself computed per render — so this does too. It
   * is two array lengths and a filter over all_damages; memoising it was never
   * worth a way for the number to disagree with the images.
   */
  const imageCount = uploadRows.length + getDamageGalleryImages().length;

  const VIEW_PRESETS = [
    { id: "threeq", label: "¾ view" },
    { id: "front", label: "Front" },
    { id: "rear", label: "Rear" },
    { id: "left", label: "Left" },
    { id: "right", label: "Right" },
    { id: "top", label: "Top" },
  ];

  const TABS = [
    {
      id: "ai",
      label: "AI summary",
      enabled: showAiSummary,
    },
    {
      id: "overview",
      label: "Damage report",
      enabled: pageConfig.tabs.overview.enabled,
    },
    {
      id: "poi",
      label: "Point of impact",
      // Shown whenever the client's API can serve it. Unlike the other tabs this
      // is not gated on pageConfig: the POI tab is redesign-only, and the shared
      // tabs block is what the existing design reads. Never for a failed case,
      // and never on a deployment whose backend has no such route.
      enabled: showPoi,
    },
    {
      id: "passport",
      label: "Digital passport",
      // Served by the legacy portal, which only Hegic has, so gated on the
      // client contract like the POI tab rather than on pageConfig.
      enabled: showPassport,
      count: passport.history?.priorCount || null,
    },
    {
      id: "images",
      label: "Images",
      count: imageCount,
      enabled: pageConfig.tabs.images.enabled,
    },
    {
      id: "documents",
      label: "Documents",
      count: enabledDocumentsCount,
      enabled: pageConfig.tabs.documents.enabled && isApproved,
    },
  ].filter((entry) => entry.enabled);

  /**
   * Keep the selected tab one that exists.
   *
   * Navigating between cases changes the route parameter without remounting,
   * so `tab` survives — and a tab that is right for one case can be absent on
   * the next. Point of impact on a good case, then a failed one, used to leave
   * the tab bar without it and the panel blank.
   */
  const tabIds = TABS.map((entry) => entry.id).join(",");
  useEffect(() => {
    const ids = tabIds ? tabIds.split(",") : [];
    if (ids.length && !ids.includes(tab)) setTab(ids[0]);
    // Keyed on the ids rather than the array: TABS is rebuilt every render,
    // so depending on it would run this on every one.
  }, [tabIds, tab]);

  /* ── Shared class fragments ───────────────────────────────────────────── */
  const cardClass =
    "rounded-card border border-cl-card bg-cl-surface overflow-hidden";
  const cardHeaderClass =
    "flex items-center justify-between gap-3 border-b border-cl-head px-4 py-3";
  const cardTitleClass = "text-[13.5px] font-semibold text-cl-text";
  const fieldLabelClass =
    "text-[9.5px] font-semibold uppercase tracking-[0.06em] text-cl-label";

  const uploads = categorizeUploads();
  const capturedImages = getCapturedImages();
  const damageGallery = getDamageGalleryImages();
  const partWiseDamageGallery = getPartWiseDamageImages();

  // Only bucket uploads that say which bucket they belong in. An empty case
  // keeps the categorised layout, so its three placeholder cards still appear
  // exactly where they always did.
  const showFlatGallery =
    capturedImages.length > 0 && !uploadsAreCategorised(capturedImages);

  /**
   * In-page targets offered by the Cammy dock. Each entry switches to the
   * owning tab and then scrolls — the scroll waits two frames so the newly
   * mounted tab content exists before we look it up.
   */
  const goToSection = useCallback((tabId, elementId) => {
    setTab(tabId);
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        document
          .getElementById(elementId)
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }),
    );
  }, []);

  // Keyed on the ids themselves, not on TABS. TABS is a plain array literal
  // rebuilt every render, so `[TABS]` changed identity every time: the Set was
  // new each render, pageSections recomputed, usePublishPageSections fired,
  // the provider setState'd, and that re-render rebuilt TABS again —
  // "Maximum update depth exceeded", on a loop.
  //
  // The visible symptom was navigation: React could never finish a re-render,
  // so clicking a sidebar link changed the URL while the screen stayed put.
  const tabIdsKey = TABS.map((tab) => tab.id).join(",");
  const enabledTabIds = useMemo(
    () => new Set(tabIdsKey ? tabIdsKey.split(",") : []),
    [tabIdsKey],
  );

  const pageSections = useMemo(() => {
    const candidates = [
      ["ai", "ai-summary", "AI summary", null, null],
      ["overview", "3d-model", "3D damage model", null, "section-3d-model"],
      [
        "overview",
        "damaged-parts",
        "Damaged parts",
        damage.damagedCount || null,
        "section-damaged-parts",
      ],
      [
        "overview",
        "damage-summary",
        "Damage summary",
        null,
        "section-damage-summary",
      ],
      ["overview", "checks", "Checks", null, "section-checks"],
      [
        "overview",
        "vehicle-information",
        "Vehicle information",
        null,
        "section-vehicle-information",
      ],
      [
        "images",
        "images",
        "Images",
        imageCount || null,
        "section-image-categories",
      ],
      [
        "images",
        "all-damage-images",
        "All damage images",
        damageGallery.length || null,
        "section-all-damage-images",
      ],
      ["documents", "documents", "Documents", null, "section-documents"],
      ["documents", "pdf-report", "PDF report", null, "section-pdf-report"],
    ];

    return candidates
      .filter(([tabId]) => enabledTabIds.has(tabId))
      .map(([tabId, id, label, hint, elementId]) => ({
        id,
        label,
        hint: hint == null ? null : String(hint),
        go: () => goToSection(tabId, elementId),
      }));
  }, [
    enabledTabIds,
    goToSection,
    damage.damagedCount,
    imageCount,
    damageGallery.length,
  ]);

  usePublishPageSections(pageSections);

  /**
   * What Parkhi shows in his speech bubble while hovered on this page —
   * independent of pageSections/the sitemap above, and of the Cases list's
   * own per-row hover summary (CasesRedesign.js). Built only from values
   * this page already computed for its own header/cards, same "whatever's
   * available" rule as the Cases list's summary.
   */
  const cammyPageSummary = useMemo(() => {
    const vehicleTitle = [make, model]
      .filter((value) => value && value !== "NA")
      .join(" ");
    const facts = [
      countryName !== "-" && { label: "Country", value: countryName },
      damage.totalParts > 0 && {
        label: "Damage",
        value: `${damage.damagedCount} of ${damage.totalParts} parts`,
      },
      aiConfidence && { label: "AI confidence", value: aiConfidence },
    ].filter(Boolean);
    return {
      title: vehicleTitle || primaryId,
      status: statusInfo.text,
      statusChip,
      facts,
    };
  }, [
    make,
    model,
    primaryId,
    countryName,
    damage.totalParts,
    damage.damagedCount,
    aiConfidence,
    statusInfo.text,
    statusChip,
  ]);
  usePublishCammyPageSummary(cammyPageSummary);

  const imageCategories = [
    {
      key: "exteriors",
      enabled: pageConfig.tabs.images.sections.categories.exteriors.enabled,
      label: pageConfig.tabs.images.sections.categories.exteriors.label,
      tag: pageConfig.tabs.images.sections.categories.exteriors.tag,
      items: uploads.exteriors,
    },
    {
      key: "closeups",
      enabled: pageConfig.tabs.images.sections.categories.closeups.enabled,
      label: pageConfig.tabs.images.sections.categories.closeups.label,
      tag: pageConfig.tabs.images.sections.categories.closeups.tag,
      items: uploads.closeups,
    },
    {
      key: "identifiers",
      enabled: pageConfig.tabs.images.sections.categories.identifiers.enabled,
      label: pageConfig.tabs.images.sections.categories.identifiers.label,
      tag: pageConfig.tabs.images.sections.categories.identifiers.tag,
      items: uploads.identifiers,
    },
  ].filter((category) => category.enabled);

  const aiCards = [
    {
      key: "vehicleOverview",
      title: pageConfig.tabs.aiSummary.cards.vehicleOverview.title,
      data: aiSummaryData?.vehicle_overview,
      empty: "No vehicle overview data available",
    },
    {
      key: "identification",
      title: pageConfig.tabs.aiSummary.cards.identification.title,
      data: aiSummaryData?.identification,
      empty: "No identification data available",
    },
    {
      key: "environmentQuality",
      title: pageConfig.tabs.aiSummary.cards.environmentQuality.title,
      data: aiSummaryData?.environment_quality,
      empty: "No environment & quality data available",
    },
    {
      key: "authenticityCheck",
      title: pageConfig.tabs.aiSummary.cards.authenticityCheck.title,
      data: aiSummaryData?.authenticity_check,
      empty: "No authenticity check data available",
      showStatus: true,
    },
    {
      key: "imageQuality",
      title: pageConfig.tabs.aiSummary.cards.imageQuality.title,
      data: aiSummaryData?.image_quality,
      empty: "No image quality data available",
      showStatus: true,
    },
    {
      key: "vehicleCondition",
      title: pageConfig.tabs.aiSummary.cards.vehicleCondition.title,
      data: aiSummaryData?.vehicle_condition,
      empty: "No vehicle condition data available",
      showStatus: true,
    },
  ].filter((card) => pageConfig.tabs.aiSummary.cards[card.key].enabled);

  const vehicleFields =
    pageConfig.tabs.overview.sections.vehicleInformation.fields;

  // Label/value pairs preserve the previous field set, fallbacks included.
  const vehicleInfoGroups = [
    {
      title: "Inspection",
      rows: [
        [vehicleFields.inspectionId, refNum],
        [vehicleFields.reviewMode, "Manual"],
        [
          vehicleFields.createdOn,
          info?.created_at ? formatFullDateTime(info.created_at) : "-",
        ],
        [vehicleFields.closedBy, "-"],
        [vehicleFields.quoteNo, pick(info, ["quote_no", "quote_number"])],
        [vehicleFields.dealerId, pick(info, ["dealer_id", "dealerid"])],
        [vehicleFields.closedOn, pick(info, ["closed_on", "closed_at"])],
      ],
    },
    {
      title: "Vehicle",
      rows: [
        [vehicleFields.brand, pick(info, ["make", "brand"])],
        [vehicleFields.model, pick(info, ["model"])],
        [
          vehicleFields.mfgYear,
          pick(info, ["manufacture_year", "mfg_year", "year"]),
        ],
        [vehicleFields.series, pick(info, ["series", "variant"])],
        [vehicleFields.licenseNo, numPlate || "-"],
        [
          vehicleFields.vinNo,
          // Same field list the Digital Passport match already uses (below) —
          // this one had drifted to a narrower set that missed vin_reading,
          // the field the real /assessment_new response actually sends.
          pick(info, ["vin", "vin_reading", "vin_no", "vin_number", "chassis_no"]),
        ],
        [vehicleFields.engineNo, pick(info, ["engine_no", "engine_number"])],
        [vehicleFields.odoReading, pick(info, ["odo_meter_reading"])],
      ],
    },
    {
      title: "Capture",
      rows: [
        [
          vehicleFields.captureDevice,
          pick(info, ["device_info", "capture_device", "user_agent"]),
        ],
        [
          vehicleFields.internetSpeed,
          pick(info, ["internet_speed", "network_speed"]),
        ],
        [
          vehicleFields.userExperience,
          pick(info, ["user_experience", "user_rating"]),
        ],
      ],
    },
  ]
    .map((group) => ({
      ...group,
      rows: group.rows.filter(([field]) => field?.enabled),
    }))
    // A group every field of which is turned off (e.g. Ergo's Capture group)
    // has nothing to show — drop it rather than rendering a bare heading.
    .filter((group) => group.rows.length > 0);

  const damageCounts = [
    {
      key: "severe",
      label: pageConfig.tabs.overview.sections.severeDamage.label,
      value: summary?.severe_damage || 0,
      enabled: pageConfig.tabs.overview.sections.severeDamage.enabled,
    },
    {
      key: "moderate",
      label: pageConfig.tabs.overview.sections.moderateDamage.label,
      value: summary?.moderate_damage || 0,
      enabled: pageConfig.tabs.overview.sections.moderateDamage.enabled,
    },
    {
      key: "minor",
      label: pageConfig.tabs.overview.sections.minorDamage.label,
      value: summary?.minor_damage || 0,
      enabled: pageConfig.tabs.overview.sections.minorDamage.enabled,
    },
  ].filter((entry) => entry.enabled);

  const showApproveButton =
    pageConfig.header.showApproveButton &&
    info?.claim_state &&
    (parseInt(info.claim_state, 10) === 3 ||
      parseInt(info.claim_state, 10) === 5);

  return (
    <MediaUrlProvider deployment={deployment}>
    <div className="flex h-full min-h-0 w-full flex-col bg-cl-bg font-clearline text-cl-text">
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 z-modal flex items-center justify-center bg-[rgba(246,247,249,0.8)]">
          <div className="text-center">
            <img
              src={loadingIcon}
              alt=""
              className="mx-auto mb-3 h-7 w-7 animate-spin"
            />
            <p className="text-[12.5px] text-cl-muted">
              Loading assessment data…
            </p>
          </div>
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col px-4 pb-4 pt-4 sm:px-8 sm:pb-[26px] sm:pt-5">
        {/* ── Header stack ─────────────────────────────────────────────── */}
        <div className="mb-[14px]">
          <div className="min-w-0 flex-1">
            {/* Only when there is a back to go to — a case opened in a new tab
                begins that tab's history, so the control would be a lie. */}
            <BackToCases className="mb-3" />

            <div className="mb-2 text-[12px] text-cl-faint">
              {/* Stays in /redesign, like the sidebar: a breadcrumb that drops
                  you into the other design is not a breadcrumb. */}
              <Link
                to={deployment?.listPath || "/redesign/cases"}
                className="transition-colors hover:text-cl-accent"
              >
                {deployment?.listLabel || "Cases"}
              </Link>
              <span className="mx-1">/</span>
              <span className="font-medium text-cl-secondary">{primaryId}</span>
            </div>

            <div className="mb-[6px] flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
              {/* The big bold title here used to repeat primaryId a third
                  time — already in the breadcrumb above and, whenever
                  there's no real plate number, identical to "Received"
                  below too (the API sends the created_at timestamp back as
                  num_plate for a case with none). Removed; the breadcrumb
                  stays the one place that identifies the case by itself. */}
              <div className="flex min-w-0 flex-wrap items-center gap-[10px]">
                <span
                  className="inline-flex flex-none items-center gap-[6px] rounded-pill px-[11px] py-1 text-[11.5px] font-semibold"
                  style={{ background: statusChip.bg, color: statusChip.fg }}
                >
                  <span
                    className="h-[6px] w-[6px] rounded-pill"
                    style={{ background: statusChip.fg }}
                  />
                  {statusInfo.text}
                </span>

                {aiConfidence && (
                  <span className="inline-flex flex-none items-center gap-[6px] rounded-pill border border-cl-card bg-cl-surface px-[11px] py-1 text-[11.5px] font-semibold text-cl-approve">
                    AI confidence {aiConfidence}
                  </span>
                )}

                <CaptureIssues
                  assessment={assessmentData}
                  isLoading={isLoading}
                  variant="clearline"
                />
              </div>

              {/* Actions — same row, same height as the title. Country and
                  upload time move down into the meta row below instead of
                  stacking here, so this row never has to grow taller than a
                  single line. */}
              <div className="flex flex-none flex-wrap items-center gap-[10px]">
                {pageConfig.header.showRecaptureButton && (
                  <button
                    type="button"
                    className="flex items-center gap-2 rounded-control border border-cl-control bg-cl-surface px-[14px] py-[9px] text-[13px] font-medium text-cl-secondary transition-colors hover:border-cl-faint hover:bg-cl-hover"
                  >
                    <Camera className="h-[15px] w-[15px] text-cl-faint" />
                    {pageConfig.header.recaptureButtonLabel}
                  </button>
                )}
                {showApproveButton && (
                  <button
                    type="button"
                    onClick={handleApproveClick}
                    className="flex items-center gap-2 rounded-control bg-cl-approve px-4 py-[9px] text-[13px] font-semibold text-white shadow-cl-approve transition-colors hover:bg-cl-approve-hover"
                  >
                    <CheckCircle2 className="h-[15px] w-[15px]" />
                    {pageConfig.header.approveButtonLabel}
                  </button>
                )}
              </div>
            </div>

            {/* Meta strip — a single bordered tile matching the card chrome
                used everywhere else on this page, rather than bare text.
                Vehicle identity (Make/Model/Inspection ID) on the left, case
                facts (Country/Time taken to upload/Received) on the right,
                split by a vertical rule on wide screens. */}
            <div className="flex flex-col gap-4 rounded-tile border border-cl-card bg-cl-surface px-4 py-3 sm:flex-row sm:items-stretch">
              <div className="flex flex-1 flex-col gap-[10px]">
                <MetaItem icon={Car} label="Make" value={make} />
                <MetaItem icon={Tag} label="Model" value={model} />
                {refNum !== primaryId && (
                  <MetaItem
                    icon={Hash}
                    label="Inspection ID"
                    value={refNum}
                    valueClassName="break-all"
                  />
                )}
              </div>

              {(countryName !== "-" || timeTakenToUpload || receivedAt) && (
                <>
                  <div className="hidden w-px self-stretch bg-cl-card sm:block" />
                  <div className="flex flex-1 flex-col gap-[10px]">
                    {countryName !== "-" && (
                      <MetaItem
                        icon={Globe}
                        label="Country"
                        value={countryName}
                        trailing={
                          countryFlag && (
                            <img
                              src={countryFlag}
                              alt=""
                              className="h-[11px] w-4 rounded-[2px] object-cover"
                            />
                          )
                        }
                      />
                    )}
                    {timeTakenToUpload && (
                      <MetaItem
                        icon={Clock}
                        label="Time taken to upload"
                        value={formatUploadDuration(timeTakenToUpload)}
                      />
                    )}
                    {receivedAt && (
                      <MetaItem icon={Inbox} label="Received" value={receivedAt} />
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="cl-scroll mb-4 flex flex-none gap-[22px] overflow-x-auto border-b border-cl-card text-[13px] font-medium text-cl-label">
          {TABS.map((entry) => {
            const isActive = tab === entry.id;
            return (
              <button
                key={entry.id}
                type="button"
                onClick={() => setTab(entry.id)}
                className={`-mb-px shrink-0 whitespace-nowrap px-[2px] pb-[10px] transition-colors ${
                  isActive
                    ? "border-b-2 border-cl-accent font-semibold text-cl-accent"
                    : "border-b-2 border-transparent hover:text-cl-secondary"
                }`}
              >
                {entry.label}
                {entry.count != null && (
                  <span className="ml-[6px] text-cl-disabled">
                    {entry.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* ── Tab content ──────────────────────────────────────────────── */}
        <div className="cl-scroll min-h-0 flex-1 overflow-y-auto">
          {/* Not on the passport tab itself — you are already looking at it. */}
          {tab !== "passport" && (
            <PassportAlert
              alert={passport.alert}
              busy={passport.loading}
              onOpen={() => setTab("passport")}
            />
          )}
          {/* Damage report — mock 3a */}
          {tab === "overview" && pageConfig.tabs.overview.enabled && (
            <div className="flex flex-col gap-4">
              <div
                id="section-3d-model"
                className="grid grid-cols-1 gap-4 xl:min-h-[560px] xl:grid-cols-[1fr_348px]"
              >
                {/* 3D damage model */}
                <div className={`${cardClass} flex flex-col`}>
                  <div className={`${cardHeaderClass} flex-wrap`}>
                    <div className="flex items-center gap-3">
                      <span className={cardTitleClass}>3D damage model</span>
                      <div
                        role="group"
                        aria-label="Vehicle model"
                        className="flex gap-1 rounded-tile bg-cl-bg p-[2px] text-[11px] font-semibold"
                      >
                        {MODEL_OPTIONS.map((option) => (
                          <button
                            key={option.id}
                            type="button"
                            onClick={() => setVehicleModel(option.id)}
                            aria-pressed={vehicleModel === option.id}
                            className={`rounded-[6px] px-[9px] py-[4px] transition-colors ${
                              vehicleModel === option.id
                                ? "bg-cl-surface text-cl-accent shadow-[0_1px_2px_rgba(19,23,32,0.08)]"
                                : "text-cl-faint hover:text-cl-secondary"
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1 text-[11px] font-semibold">
                      {VIEW_PRESETS.map((preset) => (
                        <button
                          key={preset.id}
                          type="button"
                          onClick={() => setViewPreset(preset.id)}
                          className={`rounded-[7px] px-[10px] py-[5px] transition-colors ${
                            viewPreset === preset.id
                              ? "bg-cl-tint text-cl-accent"
                              : "text-cl-faint hover:text-cl-secondary"
                          }`}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="relative min-h-[280px] flex-1 bg-cl-canvas sm:min-h-[470px]">
                    <CarDamage3D
                      model={vehicleModel}
                      damage={damage.damageMap}
                      selectedPart={selectedPart}
                      onSelectPart={selectPart}
                      viewPreset={viewPreset}
                    />
                    {/* The click affordance is pulled out of the muted line and
                        given an icon: it was previously the third of three
                        equally-weighted hints, so the one interaction a reviewer
                        actually needs read as incidental. */}
                    <div className="pointer-events-none absolute bottom-3 left-3 flex items-center gap-2 rounded-tile border border-cl-card bg-[rgba(255,255,255,0.88)] px-[11px] py-[6px] text-[11px] text-cl-muted">
                      <span className="flex items-center gap-[5px] font-semibold text-cl-text">
                        <MousePointerClick className="h-[13px] w-[13px] shrink-0 text-cl-accent" />
                        Click a panel
                      </span>
                      <span aria-hidden="true" className="text-cl-faint">
                        ·
                      </span>
                      {/* Zoom is no longer on the bare wheel — the page needs it.
                          See the wheel-gating note in CarDamage3D.jsx. */}
                      <span>drag to rotate</span>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-center gap-[14px] border-t border-cl-head px-3 py-[11px] text-[11px] font-medium text-cl-muted">
                    {[
                      ["Severe", SEVERITY_META.severe.color],
                      ["Moderate", SEVERITY_META.moderate.color],
                      ["Minor", SEVERITY_META.minor.color],
                      ["No damage", NO_DAMAGE_COLOR],
                    ].map(([label, color]) => (
                      <span key={label} className="flex items-center gap-[5px]">
                        <span
                          className="h-[9px] w-[9px] rounded-[3px]"
                          style={{ background: color }}
                        />
                        {label}
                      </span>
                    ))}
                    <span className="text-cl-disabled">|</span>
                    <span>
                      {damage.damagedCount} damaged of {damage.totalParts} parts
                    </span>
                  </div>
                </div>

                {/* Right rail.
                    Capped to the height of the visible content area so the
                    damaged-parts list scrolls inside itself instead of growing
                    the row. Without the cap a heavily damaged vehicle made the
                    rail as tall as its list — the grid row stretched to match,
                    the 3D model was dragged out to the same height, and the
                    selected part's photo at the top of the rail was pushed off
                    screen by the time you reached the bottom of the list.

                    The cap is never allowed below 560px — the same floor as
                    this grid's own xl:min-h-[560px], i.e. the 3D model card's
                    own natural height — because on a short viewport
                    (calc(100vh-165px) landing under that) a smaller cap still
                    left the model card at its natural size (it never shrinks
                    below its own min-height) while the rail rendered shorter
                    than the row it sits in, leaving a blank gap in its cell
                    above the next section. A JS-measured height was tried
                    instead of this floor and made things worse: since it read
                    the model card's height *after* layout, a long list would
                    already have stretched the model to match it via the grid's
                    default row-stretch, and the "measured" cap just confirmed
                    that inflated height instead of preventing it.

                    165px is the chrome above and below this scroll area
                    (measured: 139px header + tab bar, 26px bottom padding).
                    min-h-0 lets the list card shrink; the selected-part card is
                    flex-none so the shrinking lands on the list, not the photo. */}
                <div className="flex min-h-0 min-w-0 flex-col gap-3 xl:max-h-[max(calc(100vh-165px),560px)]">
                  {/* Selected part */}
                  <div className={`${cardClass} flex-none`}>
                    {selectedDetail ? (
                      <>
                        {/* Every angle the panel was photographed from, not
                            just the first. A lamp shot from five positions is
                            five pieces of evidence, and the one the assessment
                            happens to list first is not necessarily the one
                            that shows the damage.

                            Keyed on the part so React remounts on every
                            selection and the swap animation replays — see the
                            cl-swap-in rules in src/index.css. */}
                        {selectedDetail.severity &&
                          selectedDetail.photos.length > 0 && (
                            <div
                              key={`photo-${selectedPart}`}
                              className="cl-swap-in-image cl-scroll flex gap-[2px] overflow-x-auto"
                            >
                              {selectedDetail.photos.map((url, index) => (
                                <button
                                  key={url}
                                  type="button"
                                  title={`${selectedDetail.label} — view ${
                                    index + 1
                                  } of ${selectedDetail.photos.length}`}
                                  onClick={() =>
                                    openLightbox(
                                      selectedDetail.photos.map((photo, i) => ({
                                        id: `${selectedDetail.id}-${i}`,
                                        imageUrl: photo,
                                        title: selectedDetail.label,
                                        side: selectedDetail.side,
                                        source: "part",
                                      })),
                                      index,
                                      true,
                                    )
                                  }
                                  className={`block h-32 flex-none ${
                                    selectedDetail.photos.length === 1
                                      ? "w-full"
                                      : "w-[46%]"
                                  }`}
                                >
                                  <img
                                    src={url}
                                    alt={`${selectedDetail.label} view ${index + 1}`}
                                    loading="lazy"
                                    className="h-full w-full object-cover"
                                    onError={(e) => {
                                      e.currentTarget.closest(
                                        "button",
                                      ).style.display = "none";
                                    }}
                                  />
                                </button>
                              ))}
                            </div>
                          )}
                        <div
                          key={`body-${selectedPart}`}
                          className="cl-swap-in px-[15px] py-[13px]"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="min-w-0 text-[15px] font-bold tracking-[-0.01em] text-cl-text">
                              {selectedDetail.label}
                            </div>
                            {selectedDetail.severity ? (
                              <span
                                className="flex-none rounded-pill px-[10px] py-[3px] text-[11px] font-semibold"
                                style={{
                                  background:
                                    SEVERITY_META[selectedDetail.severity]
                                      .chipBg,
                                  color:
                                    SEVERITY_META[selectedDetail.severity]
                                      .chipFg,
                                }}
                              >
                                {SEVERITY_META[selectedDetail.severity].label}
                              </span>
                            ) : (
                              <span className="flex-none rounded-pill bg-cl-bg px-[10px] py-[3px] text-[11px] font-semibold text-cl-muted">
                                No damage
                              </span>
                            )}
                          </div>
                          <div className="mt-[2px] text-[11.5px] text-cl-faint">
                            {selectedDetail.location}
                            {selectedDetail.intensityLabel && (
                              <span> · {selectedDetail.intensityLabel}</span>
                            )}
                            {!selectedDetail.locatable && (
                              <span className="text-cl-disabled">
                                {" · not on the 3D model"}
                              </span>
                            )}
                          </div>

                          {selectedDetail.fromHistory && (
                            /* Otherwise a corrected figure reads as the AI's
                               own, and a reviewer cannot tell that the panel
                               has already been looked at by someone. */
                            <div className="mt-[7px] flex items-start gap-[6px] rounded-tile bg-cl-bg px-[9px] py-[6px] text-[11px] text-cl-secondary">
                              <Pencil className="mt-[2px] h-[10px] w-[10px] flex-none text-cl-faint" />
                              <span>
                                Corrected{" "}
                                {selectedDetail.editedBy
                                  ? `by ${selectedDetail.editedBy}`
                                  : "after assessment"}
                              </span>
                            </div>
                          )}

                          {selectedDetail.severity ? (
                            <>
                              <div className="mt-[11px] flex items-center gap-[9px]">
                                <div className="h-[6px] flex-1 overflow-hidden rounded-pill bg-cl-head">
                                  <div
                                    className="h-full rounded-pill"
                                    style={{
                                      width: `${Math.min(
                                        100,
                                        (selectedDetail.score /
                                          selectedDetail.scoreMax) *
                                          100,
                                      )}%`,
                                      background:
                                        SEVERITY_META[selectedDetail.severity]
                                          .chipFg,
                                    }}
                                  />
                                </div>
                                <span className="text-[12px] font-bold text-cl-secondary">
                                  {selectedDetail.score.toFixed(1)}
                                  <span className="font-normal text-cl-faint">
                                    /{selectedDetail.scoreMax}
                                  </span>
                                </span>
                              </div>

                              <div className="mt-3 grid grid-cols-2 gap-2">
                                <div className="rounded-tile bg-cl-bg px-[10px] py-2">
                                  <div className={fieldLabelClass}>
                                    Recommended
                                  </div>
                                  <div className="mt-[2px] text-[12.5px] font-semibold text-cl-text">
                                    {selectedDetail.action || "—"}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setEditPartOpen(true)}
                                  disabled={!selectedDetail.raw}
                                  title={`Edit ${selectedDetail.label}`}
                                  className="flex h-full items-center justify-center gap-[6px] rounded-tile bg-cl-tint px-[10px] py-2 text-[12.5px] font-semibold text-cl-accent transition-colors hover:bg-cl-accent hover:text-white disabled:cursor-not-allowed disabled:bg-cl-bg disabled:text-cl-disabled"
                                >
                                  <Pencil className="h-[13px] w-[13px]" />
                                  Edit
                                </button>
                              </div>

                              {warningCount > 0 && (
                                <div className="mt-[10px] flex items-start gap-[6px] rounded-tile bg-[#fdf6e5] px-[10px] py-[7px] text-[11.5px] text-[#8a5c07]">
                                  <AlertTriangle className="mt-[2px] h-[11px] w-[11px] flex-none" />
                                  <span>
                                    {warningCount} capture warning
                                    {warningCount > 1 ? "s" : ""} on this
                                    inspection
                                  </span>
                                </div>
                              )}

                              {selectedDetail.photoCount > 0 && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    openLightbox(
                                      selectedDetail.photos.map(
                                        (url, index) => ({
                                          id: `${selectedDetail.id}-${index}`,
                                          imageUrl: url,
                                          title: selectedDetail.label,
                                          side: selectedDetail.side,
                                          source: "part",
                                        }),
                                      ),
                                      0,
                                      true,
                                    )
                                  }
                                  className="mt-[11px] text-[12px] font-semibold text-cl-accent transition-colors hover:text-cl-accent-hover"
                                >
                                  View {selectedDetail.photoCount} photo
                                  {selectedDetail.photoCount > 1 ? "s" : ""} of
                                  this panel →
                                </button>
                              )}
                            </>
                          ) : (
                            <p className="mt-[11px] text-[12.5px] leading-[1.5] text-cl-muted">
                              No damage detected on this panel.
                              {selectedDetail.photoCount > 0
                                ? ` Surface verified across ${selectedDetail.photoCount} photos.`
                                : ""}
                            </p>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="px-[15px] py-6 text-center">
                        <div className="text-[13px] font-semibold text-cl-text">
                          No panel selected
                        </div>
                        <p className="mt-1 text-[12px] leading-[1.5] text-cl-muted">
                          Click a panel on the model, or pick a row from the
                          damaged parts list.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Damaged parts list */}
                  <div
                    id="section-damaged-parts"
                    className={`${cardClass} flex min-h-0 flex-1 flex-col`}
                  >
                    <div className="flex flex-none items-center justify-between border-b border-cl-head px-[15px] py-[11px]">
                      <span className="text-[13px] font-semibold text-cl-text">
                        Damaged parts
                      </span>
                      <span className="text-[11px] text-cl-faint">
                        click to locate
                      </span>
                    </div>

                    <div className="cl-scroll min-h-0 flex-1 overflow-y-auto">
                      {damage.damagedParts.length === 0 ? (
                        <p className="px-[15px] py-5 text-center text-[12px] text-cl-muted">
                          {isLoading
                            ? "Loading damage assessment…"
                            : "No damage detected on this vehicle."}
                        </p>
                      ) : (
                        damage.damagedParts.map((part) => {
                          const isSelected =
                            part.key != null && part.key === selectedPart;
                          return (
                            <button
                              key={part.id}
                              type="button"
                              title={
                                part.locatable
                                  ? undefined
                                  : `${part.label} is not a separate panel on the 3D model`
                              }
                              onClick={() => selectPart(part.key)}
                              className={`flex w-full cursor-pointer items-center gap-[10px] border-b border-cl-row border-l-2 py-2 pr-[15px] text-left transition-colors ${
                                isSelected
                                  ? "border-l-cl-accent bg-cl-tint pl-[13px]"
                                  : "border-l-transparent pl-[13px] hover:bg-cl-hover"
                              }`}
                            >
                              <span
                                className="h-[9px] w-[9px] flex-none rounded-[3px]"
                                style={{
                                  background:
                                    SEVERITY_META[part.severity].color,
                                }}
                              />
                              <span className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-cl-text">
                                {part.label}
                              </span>
                              <span className="flex-none truncate text-[11.5px] text-cl-faint">
                                {part.action}
                              </span>
                              <span className="w-[26px] flex-none text-right text-[12px] font-bold text-cl-secondary">
                                {part.score.toFixed(1)}
                              </span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Scene & background forensics — what the images say about the
                  circumstances of the shoot. Placed directly under the 3D model
                  row, full width, because it describes the whole image set
                  rather than the selected panel in the rail.

                  Hidden on a failed case for the same reason the point-of-impact
                  and passport tabs are: it reads the capture of a vehicle that
                  was never successfully assessed. It lives in the damage report
                  rather than the POI tab, which is why hiding that tab did not
                  already cover it. */}
              {poi?.forensics && !caseFailed && (
                <SceneForensics forensics={poi.forensics} />
              )}

              {/* Supporting detail — summary counts, checks, vehicle record */}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_348px]">
                <div id="section-damage-summary" className={cardClass}>
                  <div className={cardHeaderClass}>
                    <span className={cardTitleClass}>Damage summary</span>
                    {pageConfig.tabs.overview.sections.damagedParts.enabled && (
                      <button
                        type="button"
                        onClick={() => openDamagedPartsModal()}
                        className="text-[12px] font-semibold text-cl-accent transition-colors hover:text-cl-accent-hover"
                      >
                        Open parts list →
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-4">
                    {pageConfig.tabs.overview.sections.damagedParts.enabled && (
                      <button
                        type="button"
                        onClick={() => openDamagedPartsModal()}
                        className="rounded-tile bg-cl-bg px-3 py-[10px] text-left transition-colors hover:bg-cl-tint"
                      >
                        <div className={fieldLabelClass}>Damaged parts</div>
                        <div className="mt-1 text-[18px] font-bold text-cl-text">
                          {summary?.damaged_parts ?? damage.damagedCount}
                        </div>
                      </button>
                    )}
                    {damageCounts.map((entry) => (
                      <button
                        key={entry.key}
                        type="button"
                        onClick={() =>
                          openDamagedPartsModal("damaged", entry.key)
                        }
                        className="rounded-tile bg-cl-bg px-3 py-[10px] text-left transition-colors hover:bg-cl-tint"
                      >
                        <div className={fieldLabelClass}>{entry.label}</div>
                        <div className="mt-1 flex items-center gap-2">
                          <span
                            className="h-[9px] w-[9px] rounded-[3px]"
                            style={{
                              background: SEVERITY_META[entry.key].color,
                            }}
                          />
                          <span className="text-[18px] font-bold text-cl-text">
                            {entry.value}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div id="section-checks" className={cardClass}>
                  <div className={cardHeaderClass}>
                    <span className={cardTitleClass}>Checks</span>
                  </div>
                  <div className="flex flex-col divide-y divide-cl-row">
                    {pageConfig.tabs.overview.sections.partsIdentified
                      .enabled &&
                      (() => {
                        const tally = (
                          <>
                            <span className="text-cl-muted">Parts identified</span>
                            <span className="font-semibold text-cl-text">
                              {summary?.parts_identified || 0} of{" "}
                              {(summary?.parts_identified || 0) +
                                (summary?.non_identified_parts_count || 0)}
                            </span>
                          </>
                        );
                        // The names behind the tally are in the response and
                        // the old design listed them. A row with nothing
                        // behind it stays a plain row rather than a button
                        // that opens an empty modal.
                        return hasPartsOverview(assessmentData) ? (
                          <button
                            type="button"
                            onClick={() => setPartsListOpen(true)}
                            className="flex items-center justify-between px-4 py-[10px] text-[12.5px] transition-colors hover:bg-cl-hover"
                          >
                            {tally}
                          </button>
                        ) : (
                          <div className="flex items-center justify-between px-4 py-[10px] text-[12.5px]">
                            {tally}
                          </div>
                        );
                      })()}
                    {pageConfig.tabs.overview.sections.sidesIdentified
                      .enabled &&
                      (() => {
                        const sides = sidesOverview(assessmentData);
                        const tally = (
                          <>
                            <span className="text-cl-muted">Sides identified</span>
                            <span className="font-semibold text-cl-text">
                              {sides.identified.length} of{" "}
                              {sides.identified.length + sides.unidentified.length}
                            </span>
                          </>
                        );
                        return hasSidesOverview(assessmentData) ? (
                          <button
                            type="button"
                            onClick={() => setSidesListOpen(true)}
                            className="flex items-center justify-between px-4 py-[10px] text-[12.5px] transition-colors hover:bg-cl-hover"
                          >
                            {tally}
                          </button>
                        ) : (
                          <div className="flex items-center justify-between px-4 py-[10px] text-[12.5px]">
                            {tally}
                          </div>
                        );
                      })()}
                    {pageConfig.tabs.overview.sections.locationCheck
                      .enabled &&
                      (() => {
                        // Checked against both places a case can say this
                        // (see hasNoLocationPermissionWarning) so this agrees
                        // with what Capture Issues, right above, already
                        // shows — nothing behind the map to check in that
                        // case, so disable rather than open an empty one.
                        const locationPermissionDenied =
                          hasNoLocationPermissionWarning(assessmentData);
                        return locationPermissionDenied ? (
                          <div className="flex items-center justify-between px-4 py-[10px] text-[12.5px] opacity-60">
                            <span className="text-cl-muted">Location check</span>
                            <span className="font-semibold text-cl-secondary">
                              Not provided
                            </span>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setIsOpen(true)}
                            className="flex items-center justify-between px-4 py-[10px] text-[12.5px] transition-colors hover:bg-cl-hover"
                          >
                            <span className="text-cl-muted">Location check</span>
                            <span className="font-semibold text-cl-accent">
                              View map →
                            </span>
                          </button>
                        );
                      })()}
                    {pageConfig.tabs.overview.sections.searchParts.enabled && (
                      <button
                        type="button"
                        onClick={() => {
                          setIsSearchActive(true);
                          openDamagedPartsModal();
                        }}
                        className="flex items-center justify-between px-4 py-[10px] text-[12.5px] transition-colors hover:bg-cl-hover"
                      >
                        <span className="text-cl-muted">Search parts</span>
                        <span className="font-semibold text-cl-accent">
                          Open →
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {pageConfig.tabs.overview.sections.vehicleInformation.enabled && (
                <div id="section-vehicle-information" className={cardClass}>
                  <div className={cardHeaderClass}>
                    <span className={cardTitleClass}>
                      {
                        pageConfig.tabs.overview.sections.vehicleInformation
                          .title
                      }
                    </span>
                  </div>
                  {/* One flat grid, no per-group subheadings — the groups
                      still decide field order (Inspection, then Vehicle, then
                      Capture), they just no longer get their own labelled
                      section, which read as three lopsided columns once most
                      of the fields in each were turned off. */}
                  <dl className="grid grid-cols-1 gap-x-8 gap-y-5 p-4 sm:grid-cols-2 lg:grid-cols-3">
                    {vehicleInfoGroups.flatMap((group) => group.rows).map(
                      ([field, value]) => (
                        <div key={field.label}>
                          <dt className={fieldLabelClass}>{field.label}</dt>
                          <dd className="mt-[2px] break-words text-[12.5px] text-cl-text">
                            {value === "-" || !value ? "NA" : value}
                          </dd>
                        </div>
                      ),
                    )}
                  </dl>
                </div>
              )}
            </div>
          )}

          {/* AI summary */}
          {tab === "ai" && showAiSummary && (
            <div>
              {isAiSummaryLoading ? (
                <div className="flex min-h-[400px] items-center justify-center">
                  <div className="text-center">
                    <img
                      src={loadingIcon}
                      alt=""
                      className="mx-auto mb-3 h-7 w-7 animate-spin"
                    />
                    <p className="text-[12.5px] text-cl-muted">
                      Loading AI summary…
                    </p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {aiCards.map((card) => (
                    <div key={card.key} className={cardClass}>
                      <div className={cardHeaderClass}>
                        <span className={cardTitleClass}>{card.title}</span>
                        {card.showStatus && card.data?.status && (
                          <span
                            className="h-[9px] w-[9px] rounded-pill"
                            style={{
                              background:
                                card.data.status === "pass"
                                  ? "#1d9a50"
                                  : card.data.status === "fail"
                                    ? SEVERITY_META.severe.color
                                    : SEVERITY_META.minor.color,
                            }}
                          />
                        )}
                      </div>
                      <div className="px-4 py-3">
                        {card.data?.details?.length ? (
                          <ul className="flex flex-col gap-[6px]">
                            {card.data.details.map((detail, index) => (
                              <li
                                key={index}
                                className="flex gap-2 text-[12.5px] leading-[1.5] text-cl-secondary"
                              >
                                <span className="text-cl-disabled">•</span>
                                <span className="min-w-0 break-words">
                                  {detail}
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-[12px] text-cl-faint">
                            {card.empty}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Images */}
          {tab === "poi" && showPoi && (
            <div className="flex flex-col gap-4">
              <PointOfImpact poi={poi} loading={isPoiLoading} />
            </div>
          )}

          {tab === "passport" && showPassport && (
            <div className="flex flex-col gap-4">
              {/* pick() yields placeholder strings like "-" for missing fields,
                  which are truthy and would defeat the POI fallback below — the
                  plate prefilled as a literal "-". Treat placeholders as absent. */}
              <DigitalPassport
                refNum={caseId}
                initialHistory={passport.history}
                vin={realValue(
                  pick(info, ["vin", "vin_reading", "vin_no", "chassis_no"]),
                )}
                numPlate={
                  realValue(
                    pick(info, ["num_plate", "number_plate", "registration_no"]),
                  ) || poi?.forensics?.identity?.numberPlate
                }
              />
            </div>
          )}

          {tab === "images" && pageConfig.tabs.images.enabled && (
            <div className="flex flex-col gap-4">
              <div className="flex justify-end">
                <div className="flex items-center gap-2 rounded-tile border border-cl-card bg-cl-surface px-3 py-[6px]">
                  <span className="text-[10.5px] font-semibold uppercase tracking-[0.04em] text-cl-label">
                    User Captured Images
                  </span>
                  <span className="text-[12.5px] font-semibold text-cl-text">
                    {userCapturedImageCount}
                  </span>
                </div>
              </div>
              {isLoading ? (
                <div className="flex min-h-[400px] items-center justify-center">
                  <div className="text-center">
                    <img
                      src={loadingIcon}
                      alt=""
                      className="mx-auto mb-3 h-7 w-7 animate-spin"
                    />
                    <p className="text-[12.5px] text-cl-muted">
                      Loading damage inventory…
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {showFlatGallery ? (
                    <div id="section-image-categories" className={cardClass}>
                      <div className={cardHeaderClass}>
                        <span className={cardTitleClass}>
                          {pageConfig.tabs.images.sections.capturedImages
                            ?.title || "Images captured"}
                        </span>
                        <span className="text-[11px] text-cl-faint">
                          {capturedImages.length} image
                          {capturedImages.length === 1 ? "" : "s"}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                        {capturedImages.map((imageData, index) => (
                          <button
                            key={`captured-image-${imageData.id}`}
                            type="button"
                            title={imageData.fileName}
                            onClick={() => openLightbox(capturedImages, index)}
                            className="overflow-hidden rounded-tile border border-cl-card bg-cl-surface text-left transition-colors hover:border-cl-accent"
                          >
                            <img
                              src={imageData.imageUrl}
                              alt={imageData.title}
                              loading="lazy"
                              className="h-[86px] w-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                            <div className="truncate px-2 py-[6px] text-center text-[10.5px] font-medium uppercase tracking-[0.04em] text-cl-label">
                              {imageData.title}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div
                      id="section-image-categories"
                      className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
                    >
                      {imageCategories.map((category) => (
                        <div key={category.key} className={cardClass}>
                          <button
                            type="button"
                            disabled={category.items.length === 0}
                            onClick={() => openLightbox(category.items, 0)}
                            className={`block w-full ${
                              category.items.length > 0
                                ? "cursor-pointer"
                                : "cursor-default"
                            }`}
                          >
                            {category.items.length > 0 ? (
                              <img
                                src={category.items[0].imageUrl}
                                alt={`${category.label} preview`}
                                className="h-[220px] w-full object-cover transition-opacity hover:opacity-90"
                              />
                            ) : (
                              <div className="flex h-[220px] w-full flex-col items-center justify-center bg-cl-bg">
                                <p className="text-[12.5px] font-semibold text-cl-muted">
                                  No images available
                                </p>
                                <p className="mt-1 text-[11px] text-cl-faint">
                                  User did not upload
                                </p>
                              </div>
                            )}
                          </button>
                          <div className="px-4 py-3">
                            <div className={fieldLabelClass}>{category.tag}</div>
                            <h3 className="mt-1 text-[13.5px] font-semibold text-cl-text">
                              {category.label}
                              <span className="ml-[6px] font-normal text-cl-faint">
                                {category.items.length} image
                                {category.items.length === 1 ? "" : "s"}
                              </span>
                            </h3>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {pageConfig.tabs.images.sections.allDamageImages.enabled &&
                    (() => {
                      const activeGallery =
                        damageTab === "inventory"
                          ? damageGallery
                          : partWiseDamageGallery;

                      return (
                        <div id="section-all-damage-images" className={cardClass}>
                          <div className={`${cardHeaderClass} flex-wrap`}>
                            <div
                              role="group"
                              aria-label="Damage gallery"
                              className="flex gap-1 rounded-tile bg-cl-bg p-[2px] text-[11px] font-semibold"
                            >
                              <button
                                type="button"
                                onClick={() => setDamageTab("inventory")}
                                aria-pressed={damageTab === "inventory"}
                                className={`flex items-center gap-[6px] rounded-[6px] px-[9px] py-[4px] transition-colors ${
                                  damageTab === "inventory"
                                    ? "bg-cl-surface text-cl-text shadow-[0_1px_2px_rgba(19,23,32,0.08)]"
                                    : "text-cl-faint hover:text-cl-secondary"
                                }`}
                              >
                                <span>
                                  {
                                    pageConfig.tabs.images.sections
                                      .allDamageImages.title
                                  }
                                </span>
                                <span
                                  className={`rounded-full px-[6px] py-[1px] text-[10px] ${
                                    damageTab === "inventory"
                                      ? "bg-cl-tint text-cl-accent"
                                      : "bg-cl-bg text-cl-faint"
                                  }`}
                                >
                                  {damageGallery.length}
                                </span>
                              </button>
                              <button
                                type="button"
                                onClick={() => setDamageTab("part-wise")}
                                aria-pressed={damageTab === "part-wise"}
                                className={`flex items-center gap-[6px] rounded-[6px] px-[9px] py-[4px] transition-colors ${
                                  damageTab === "part-wise"
                                    ? "bg-cl-surface text-cl-text shadow-[0_1px_2px_rgba(19,23,32,0.08)]"
                                    : "text-cl-faint hover:text-cl-secondary"
                                }`}
                              >
                                <span>Part-wise Damages</span>
                                <span
                                  className={`rounded-full px-[6px] py-[1px] text-[10px] ${
                                    damageTab === "part-wise"
                                      ? "bg-cl-tint text-cl-accent"
                                      : "bg-cl-bg text-cl-faint"
                                  }`}
                                >
                                  {partWiseDamageGallery.length}
                                </span>
                              </button>
                            </div>
                          </div>
                          {activeGallery.length > 0 ? (
                            <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                              {activeGallery.map((imageData, index) => (
                                <button
                                  key={`damage-image-${index}-${imageData.side}-${imageData.source}`}
                                  type="button"
                                  title={`${imageData.title || imageData.side} (${imageData.source})`}
                                  onClick={() =>
                                    openLightbox(activeGallery, index, true)
                                  }
                                  className="overflow-hidden rounded-tile border border-cl-card bg-cl-surface text-left transition-colors hover:border-cl-accent"
                                >
                                  <img
                                    src={imageData.imageUrl}
                                    alt={`${imageData.title || imageData.side} damage view`}
                                    className="h-[86px] w-full object-cover"
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                    }}
                                  />
                                  <div className="truncate px-2 py-[6px] text-center text-[10.5px] font-medium uppercase tracking-[0.04em] text-cl-label">
                                    {imageData.title || imageData.side}
                                  </div>
                                </button>
                              ))}
                            </div>
                          ) : (
                            <p className="px-4 py-8 text-center text-[12.5px] text-cl-muted">
                              {damageTab === "inventory"
                                ? "No inventory damage images available"
                                : "No part-wise damage images available"}
                            </p>
                          )}
                        </div>
                      );
                    })()}
                </>
              )}
            </div>
          )}

          {/* Documents */}
          {tab === "documents" && pageConfig.tabs.documents.enabled && isApproved && (
            <div className="flex flex-col gap-4">
              <div id="section-documents" className={cardClass}>
                <div className={cardHeaderClass}>
                  <span className={cardTitleClass}>Documents</span>
                  {pageConfig.tabs.documents.showDownloadAll && (
                    <button
                      type="button"
                      onClick={downloadPDFReport}
                      className="flex items-center gap-2 rounded-control border border-cl-control bg-cl-surface px-[14px] py-[7px] text-[12.5px] font-medium text-cl-secondary transition-colors hover:border-cl-faint hover:bg-cl-hover"
                    >
                      <Download className="h-[14px] w-[14px] text-cl-faint" />
                      {pageConfig.tabs.documents.downloadAllLabel}
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 lg:grid-cols-6">
                  {Object.entries(pageConfig.tabs.documents.documentTypes)
                    // This tile has no onClick at all — for any document
                    // type, not just this one — so for Ergo, where pdfReport
                    // is the only type enabled by default, it was a box with
                    // nothing behind it. The PDF preview card below (with its
                    // own fetch, loading state and actual download) is the
                    // real, working feature and is left exactly as it was.
                    .filter(
                      ([key, documentType]) =>
                        documentType.enabled &&
                        !(hidePdfReportBox && key === "pdfReport"),
                    )
                    .map(([key, documentType]) => (
                      <div
                        key={key}
                        className="flex flex-col items-center gap-2 rounded-tile border border-cl-card bg-cl-bg px-3 py-4 text-center"
                      >
                        <span className="flex h-9 w-9 items-center justify-center rounded-tile bg-cl-tint text-cl-accent">
                          <Download className="h-4 w-4" />
                        </span>
                        <span className="text-[12px] font-medium leading-tight text-cl-text">
                          {documentType.label}
                        </span>
                      </div>
                    ))}
                </div>
              </div>

              {pageConfig.tabs.documents.documentTypes.pdfReport.enabled && (
                <div id="section-pdf-report" className={cardClass}>
                  <div className={cardHeaderClass}>
                    <div>
                      <span className={cardTitleClass}>PDF preview</span>
                      {documentsArchiveName && (
                        <div className="mt-[2px] text-[11px] text-cl-faint">
                          {documentsArchiveName}
                        </div>
                      )}
                    </div>
                  </div>
                  {isDocumentsLoading ? (
                    <div className="flex h-[560px] items-center justify-center bg-cl-bg">
                      <div className="text-center">
                        <img
                          src={loadingIcon}
                          alt=""
                          className="mx-auto mb-3 h-7 w-7 animate-spin"
                        />
                        <p className="text-[12.5px] text-cl-muted">
                          Loading PDF preview…
                        </p>
                      </div>
                    </div>
                  ) : documentsPreviewUrl ? (
                    <iframe
                      title="PDF preview"
                      src={documentsPreviewUrl}
                      className="h-[560px] w-full bg-cl-surface"
                    />
                  ) : (
                    <div className="flex h-[560px] items-center justify-center bg-cl-bg px-6 text-center">
                      <p className="text-[12.5px] text-cl-muted">
                        {documentsPreviewError}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <LocationCheckModal
        assessmentData={assessmentData}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      />

      <OverviewIdentifiedModal
        isOpen={partsListOpen}
        onClose={() => setPartsListOpen(false)}
        title="Parts Identified"
        emptyMessage="No parts data available."
        sections={[
          {
            title: "Identified Parts",
            items: partsOverview(assessmentData).identified.map((part) => ({
              label: part,
            })),
            icon: checkCircleGreen,
            emptyMessage: "No identified parts",
          },
          {
            title: "Unidentified Parts",
            items: partsOverview(assessmentData).unidentified.map((part) => ({
              label: part,
            })),
            icon: warningTriangleRed,
            emptyMessage: "No unidentified parts",
          },
        ]}
      />

      <OverviewIdentifiedModal
        isOpen={sidesListOpen}
        onClose={() => setSidesListOpen(false)}
        title="Sides Identified"
        emptyMessage="No sides data available."
        sections={[
          {
            title: "Identified Sides",
            items: sidesOverview(assessmentData).identified.map((side) => ({
              label: side,
            })),
            icon: checkCircleGreen,
            emptyMessage: "No identified sides",
          },
          {
            title: "Unidentified Sides",
            items: sidesOverview(assessmentData).unidentified.map((side) => ({
              label: side,
            })),
            icon: warningTriangleRed,
            emptyMessage: "No unidentified sides",
          },
        ]}
      />

      {/* Image Lightbox */}
      <ImageLightbox
        isOpen={lightboxOpen}
        images={lightboxImages}
        currentIndex={lightboxCurrentIndex}
        onClose={closeLightbox}
        assessmentData={assessmentData}
        isDamageImages={lightboxIsDamageImages}
      />

      {/* Damaged Parts Modal */}
      <DamagedPartsModal
        isOpen={damagedPartsModalState.isOpen}
        onClose={closeDamagedPartsModal}
        assessmentData={assessmentData}
        initialFilter={damagedPartsModalState.initialFilter}
        initialSeverityFilter={damagedPartsModalState.initialSeverityFilter}
        onRefreshAssessment={refreshAssessmentData}
        setIsSearchActive={setIsSearchActive}
        isSearchActive={isSearchActive}
      />

      {/* Edit the selected panel's assessment, from the Damage report rail */}
      <EditPartModal
        isOpen={editPartOpen}
        onClose={() => setEditPartOpen(false)}
        partData={selectedDetail?.raw}
        assessmentData={assessmentData}
        onRefreshAssessment={refreshAssessmentData}
      />

      <ApproveCommentModal
        isOpen={approveCommentOpen}
        onClose={() => setApproveCommentOpen(false)}
        onSubmit={handleApproveSubmit}
        userId={getUserProfile()?.email || getClientEntry(localStorage.getItem("source_name"))?.fallbackEmail || "camcom@camcom.ai"}
        numPlate={numPlate || null}
        title="Add a note to complete the process"
        submitLabel="Submit"
      />
    </div>
    </MediaUrlProvider>
  );
}
